import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Partners from "@/pages/Partners";
import ProviderNetwork from "@/pages/ProviderNetwork";
import Quotations from "@/pages/Quotations";
import QuotationNew from "@/pages/QuotationNew";
import Documents from "@/pages/Documents";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Tasks from "@/pages/Tasks";
import PartnerCommissions from "@/pages/PartnerCommissions";
import Commissions from "@/pages/Commissions";
import Leaderboard from "@/pages/Leaderboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import PatientPortal from "@/pages/PatientPortal";

// Visa module
import VisaRequests from "@/pages/visa/VisaRequests";
import VisaRequestNew from "@/pages/visa/VisaRequestNew";
import VisaRequestDetail from "@/pages/visa/VisaRequestDetail";
import VisaStatusTracker from "@/pages/visa/VisaStatusTracker";
import VisaTravel from "@/pages/visa/VisaTravel";
import AirportPickup from "@/pages/visa/AirportPickup";
import HotelAccommodation from "@/pages/visa/HotelAccommodation";
import VisaDashboard from "@/pages/visa/VisaDashboard";
import VisaTemplates from "@/pages/visa/VisaTemplates";

const ROLE_HOME = { admin: "/admin", coordinator: "/coordinator", partner: "/partner", patient: "/portal" };

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#5C6660]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_HOME[user.role] || "/login"} replace />;
}

function StaffRoutes({ basePath, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <Layout />
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Admin */}
          <Route path="/admin" element={<StaffRoutes roles={["admin"]} />}>
            <Route index element={<Dashboard role="admin" />} />
            <Route path="leads" element={<Leads role="admin" />} />
            <Route path="leads/:id" element={<LeadDetail role="admin" />} />
            <Route path="patients" element={<Patients role="admin" />} />
            <Route path="patients/:id" element={<PatientDetail role="admin" />} />
            <Route path="partners" element={<Partners role="admin" />} />
            <Route path="network" element={<ProviderNetwork role="admin" />} />
            <Route path="quotations" element={<Quotations role="admin" />} />
            <Route path="quotations/new" element={<QuotationNew role="admin" />} />
            <Route path="commissions" element={<Commissions />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="documents" element={<Documents role="admin" />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="analytics" element={<Analytics role="admin" />} />
            <Route path="settings" element={<Settings />} />
            {/* Visa module */}
            <Route path="visa" element={<VisaRequests />} />
            <Route path="visa/new" element={<VisaRequestNew />} />
            <Route path="visa/tracker" element={<VisaStatusTracker />} />
            <Route path="visa/travel" element={<VisaTravel />} />
            <Route path="visa/pickup" element={<AirportPickup />} />
            <Route path="visa/hotel" element={<HotelAccommodation />} />
            <Route path="visa/dashboard" element={<VisaDashboard />} />
            <Route path="visa/templates" element={<VisaTemplates />} />
            <Route path="visa/:id" element={<VisaRequestDetail />} />
          </Route>

          {/* Coordinator */}
          <Route path="/coordinator" element={<StaffRoutes roles={["coordinator"]} />}>
            <Route index element={<Dashboard role="coordinator" />} />
            <Route path="leads" element={<Leads role="coordinator" />} />
            <Route path="leads/:id" element={<LeadDetail role="coordinator" />} />
            <Route path="patients" element={<Patients role="coordinator" />} />
            <Route path="patients/:id" element={<PatientDetail role="coordinator" />} />
            <Route path="network" element={<ProviderNetwork role="coordinator" />} />
            <Route path="quotations" element={<Quotations role="coordinator" />} />
            <Route path="quotations/new" element={<QuotationNew role="coordinator" />} />
            <Route path="documents" element={<Documents role="coordinator" />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="settings" element={<Settings />} />
            <Route path="visa" element={<VisaRequests />} />
            <Route path="visa/new" element={<VisaRequestNew />} />
            <Route path="visa/tracker" element={<VisaStatusTracker />} />
            <Route path="visa/travel" element={<VisaTravel />} />
            <Route path="visa/pickup" element={<AirportPickup />} />
            <Route path="visa/hotel" element={<HotelAccommodation />} />
            <Route path="visa/dashboard" element={<VisaDashboard />} />
            <Route path="visa/:id" element={<VisaRequestDetail />} />
          </Route>

          {/* Partner */}
          <Route path="/partner" element={<StaffRoutes roles={["partner"]} />}>
            <Route index element={<Dashboard role="partner" />} />
            <Route path="leads" element={<Leads role="partner" />} />
            <Route path="leads/:id" element={<LeadDetail role="partner" />} />
            <Route path="patients" element={<Patients role="partner" />} />
            <Route path="patients/:id" element={<PatientDetail role="partner" />} />
            <Route path="commissions" element={<PartnerCommissions />} />
            <Route path="settings" element={<Settings />} />
            <Route path="visa" element={<VisaRequests />} />
            <Route path="visa/new" element={<VisaRequestNew />} />
            <Route path="visa/:id" element={<VisaRequestDetail />} />
          </Route>

          {/* Patient portal */}
          <Route path="/portal" element={<StaffRoutes roles={["patient"]} />}>
            <Route index element={<PatientPortal />} />
            <Route path="settings" element={<Settings />} />
            <Route path="visa" element={<VisaRequests />} />
            <Route path="visa/new" element={<VisaRequestNew />} />
            <Route path="visa/:id" element={<VisaRequestDetail />} />
          </Route>

          <Route path="/" element={<RoleRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
